import React, { useState } from 'react';
import {
  Container, Box, Typography, TextField, Button, Select, MenuItem,
  FormControl, InputLabel, CircularProgress, Alert, Paper, Chip, Divider,
} from '@mui/material';
import { Translate as TranslateIcon, Download as DownloadIcon } from '@mui/icons-material';
import axios from 'axios';

// Remotion player — loaded lazily to avoid SSR issues
let PlayerComponent = null;
let RemotionComposition = null;

const LANGUAGES = [
  'English', 'Spanish', 'French', 'German', 'Japanese',
  'Korean', 'Portuguese', 'Italian', 'Arabic', 'Hindi',
];

const PLAYER_WIDTH = 600;
const PLAYER_HEIGHT = 600;

const ImageTranslatorPage = () => {
  const [imageUrl, setImageUrl] = useState('');
  const [targetLanguage, setTargetLanguage] = useState('English');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [playerReady, setPlayerReady] = useState(false);

  // Lazy-load Remotion to avoid crashing if package is not yet installed
  const loadRemotionPlayer = async () => {
    if (!PlayerComponent) {
      try {
        const { Player } = await import('@remotion/player');
        const { TranslationComposition } = await import('../components/ImageTranslator/TranslationComposition');
        PlayerComponent = Player;
        RemotionComposition = TranslationComposition;
        setPlayerReady(true);
      } catch {
        setPlayerReady(false);
      }
    } else {
      setPlayerReady(true);
    }
  };

  const handleTranslate = async () => {
    if (!imageUrl.trim()) {
      setError('Please enter an image URL');
      return;
    }
    setError('');
    setLoading(true);
    setResult(null);

    try {
      const response = await axios.post('/api/image-translate', {
        imageUrl: imageUrl.trim(),
        targetLanguage,
      });
      setResult(response.data);
      await loadRemotionPlayer();
    } catch (err) {
      setError(err.response?.data?.error || 'Translation failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!result?.imageUrl) return;
    // Open the original image in a new tab — full canvas export requires Remotion CLI
    window.open(result.imageUrl, '_blank');
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Image Text Translator
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Paste a product image URL to extract and translate any embedded text (e.g. Chinese labels).
          Translated text is overlaid on the image using Remotion.
        </Typography>

        {/* Input section */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Product Image URL"
              variant="outlined"
              fullWidth
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/product-image.jpg"
              onKeyDown={(e) => e.key === 'Enter' && handleTranslate()}
            />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Target Language</InputLabel>
                <Select
                  value={targetLanguage}
                  label="Target Language"
                  onChange={(e) => setTargetLanguage(e.target.value)}
                >
                  {LANGUAGES.map((lang) => (
                    <MenuItem key={lang} value={lang}>{lang}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <TranslateIcon />}
                onClick={handleTranslate}
                disabled={loading}
              >
                {loading ? 'Translating...' : 'Translate Image'}
              </Button>
            </Box>
          </Box>
        </Paper>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Results section */}
        {result && (
          <Box>
            <Divider sx={{ mb: 3 }} />

            {/* Metadata */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
              <Chip label={`Source: ${result.sourceLanguage}`} color="primary" variant="outlined" />
              <Chip label={`Target: ${result.targetLanguage}`} color="secondary" variant="outlined" />
              <Chip
                label={result.hasText ? `${result.regions?.length || 0} text regions found` : 'No text detected'}
                color={result.hasText ? 'success' : 'default'}
                variant="outlined"
              />
            </Box>

            {result.hasText ? (
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                {/* Remotion player with overlays */}
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Translated Overlay Preview
                  </Typography>
                  <Box
                    sx={{
                      width: PLAYER_WIDTH,
                      height: PLAYER_HEIGHT,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    {playerReady && PlayerComponent && RemotionComposition ? (
                      <PlayerComponent
                        component={RemotionComposition}
                        inputProps={{
                          imageUrl: result.imageUrl,
                          regions: result.regions,
                        }}
                        durationInFrames={1}
                        compositionWidth={PLAYER_WIDTH}
                        compositionHeight={PLAYER_HEIGHT}
                        fps={1}
                        style={{ width: '100%', height: '100%' }}
                        controls={false}
                        autoPlay={false}
                      />
                    ) : (
                      /* Fallback plain overlay if Remotion not loaded */
                      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
                        <img
                          src={result.imageUrl}
                          alt="Product"
                          style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                        {result.regions.map((r, i) => (
                          <Box
                            key={i}
                            sx={{
                              position: 'absolute',
                              left: `${r.x}%`,
                              top: `${r.y}%`,
                              width: `${r.width}%`,
                              minHeight: `${r.height}%`,
                              bgcolor: 'rgba(0,0,0,0.72)',
                              color: '#fff',
                              fontSize: 12,
                              p: '2px 5px',
                              borderRadius: 0.5,
                              boxSizing: 'border-box',
                              textAlign: 'center',
                            }}
                          >
                            {r.translated}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                  <Button
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    sx={{ mt: 1 }}
                    variant="outlined"
                    size="small"
                  >
                    View Original
                  </Button>
                </Box>

                {/* Text breakdown */}
                <Box sx={{ flex: 1, minWidth: 240 }}>
                  <Typography variant="subtitle2" gutterBottom>Original Text</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#fafafa' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {result.originalText || '—'}
                    </Typography>
                  </Paper>
                  <Typography variant="subtitle2" gutterBottom>Translated Text</Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#f0fff4' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {result.translatedText || '—'}
                    </Typography>
                  </Paper>

                  {result.regions.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                        Region Breakdown
                      </Typography>
                      {result.regions.map((r, i) => (
                        <Paper key={i} variant="outlined" sx={{ p: 1, mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Region {i + 1} ({r.x}%, {r.y}%)
                          </Typography>
                          <Typography variant="body2">
                            <b>Original:</b> {r.original}
                          </Typography>
                          <Typography variant="body2">
                            <b>Translated:</b> {r.translated}
                          </Typography>
                        </Paper>
                      ))}
                    </>
                  )}
                </Box>
              </Box>
            ) : (
              <Alert severity="info">
                No text was detected in this image, or it is already in {targetLanguage}.
              </Alert>
            )}
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default ImageTranslatorPage;
